/**
 * CR282: App Asset Public URL Resolver
 * 
 * Resolves app_assets.url (which may be a storage key or absolute URL)
 * into a usable public URL for rendering.
 * 
 * If url is a key like "worth-it.svg", builds the full Supabase Storage URL.
 * If url is already "https://...", returns it as-is.
 */

const BUCKET_NAME = "app-assets";

/**
 * Get the full public URL for an app asset.
 * 
 * @param asset - Asset object with optional url property
 * @returns Full public URL, or null if no url
 */
export function getAppAssetPublicUrl(asset?: { url?: string | null } | null): string | null {
  const key = asset?.url?.trim();
  if (!key) return null;

  // Already an absolute URL - use as-is
  if (key.startsWith("http://") || key.startsWith("https://")) {
    return key;
  }

  // Build full Supabase Storage URL from key
  const base = import.meta.env.VITE_SUPABASE_URL;
  if (!base) {
    console.warn("[getAppAssetPublicUrl] VITE_SUPABASE_URL not set");
    return null;
  }

  // Remove leading slashes from key and construct URL
  const cleanKey = key.replace(/^\/+/, "");
  return `${base}/storage/v1/object/public/${BUCKET_NAME}/${cleanKey}`;
}

/**
 * Get image source for an asset, preferring URL over inline SVG.
 * 
 * @param asset - Asset object with url and/or svg
 * @returns Image source URL (absolute URL or data URI), or null
 */
export function getAssetImageSrc(asset?: { url?: string | null; svg?: string | null } | null): string | null {
  if (!asset) return null;

  // Prefer resolved URL
  const resolvedUrl = getAppAssetPublicUrl(asset);
  if (resolvedUrl) return resolvedUrl;

  // Fall back to inline SVG as data URI
  if (asset.svg) {
    return `data:image/svg+xml,${encodeURIComponent(asset.svg)}`;
  }

  return null;
}
